/**
 * Standings / Stats / Records page.
 */
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Trophy } from "lucide-react";
import { api, useFetch } from "../lib/api";
import { EmptyState, Reveal } from "../components/atoms";

const TABS = [
    { id: "drivers", label: "Drivers" },
    { id: "constructors", label: "Constructors" },
    { id: "records", label: "Records" },
];

/**
 * Which block a standings tab shows: an API failure and an empty table are distinct, visible states.
 */
export function getStandingsViewState({ loading, error, rows }) {
    if (loading) return "loading";
    if (error) return "error";
    if (!rows || (Array.isArray(rows) ? rows.length === 0 : Object.keys(rows).length === 0)) return "empty";
    return "list";
}

function StandingsFallback({ viewState, what }) {
    if (viewState === "loading") {
        return (
            <div className="space-y-3" data-testid="standings-loading">
                {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="h-16 rounded-xl shimmer" />
                ))}
            </div>
        );
    }
    if (viewState === "error") {
        return (
            <EmptyState
                title={`${what} unavailable`}
                message="The standings API did not respond. Refresh the page — if this keeps happening the backend may be down."
                icon={Trophy}
            />
        );
    }
    return <EmptyState title={`No ${what.toLowerCase()} yet`} message="Nothing has been published for this season so far." icon={Trophy} />;
}

export default function Standings() {
    const standings = useFetch(() => api.standings(), []);
    const records = useFetch(() => api.records(), []);
    const { data: seasonData } = useFetch(() => api.currentSeason(), []);
    const [tab, setTab] = useState("drivers");
    const season = seasonData?.season || new Date().getFullYear();
    const data = standings.data;
    const rec = records.data;
    const driverState = getStandingsViewState({ loading: standings.loading, error: standings.error, rows: data?.drivers });
    const constructorState = getStandingsViewState({ loading: standings.loading, error: standings.error, rows: data?.constructors });
    const recordState = getStandingsViewState({ loading: records.loading, error: records.error, rows: rec?.records });

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="standings-page">
            <div className="flex flex-col gap-2 mb-10">
                <span className="label-eyebrow text-f1-red">{season} STATS & RECORDS</span>
                <h1 className="font-display text-5xl sm:text-6xl tracking-tight leading-none">
                    Numbers don't lie.
                </h1>
            </div>

            <div className="flex items-center bg-white/5 rounded-full border border-white/10 p-1 w-fit mb-8">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        data-testid={`standings-tab-${t.id}`}
                        className={`relative px-4 py-1.5 text-xs font-mono tracking-widest uppercase rounded-full transition-colors ${
                            tab === t.id ? "text-white" : "text-zinc-500"
                        }`}
                    >
                        {tab === t.id && (
                            <motion.div layoutId="standings-pill" className="absolute inset-0 bg-f1-red rounded-full" />
                        )}
                        <span className="relative">{t.label}</span>
                    </button>
                ))}
            </div>

            {tab === "drivers" && (driverState === "list" ? <DriverStandings rows={data.drivers} /> : <StandingsFallback viewState={driverState} what="Driver standings" />)}
            {tab === "constructors" && (constructorState === "list" ? <ConstructorStandings rows={data.constructors} /> : <StandingsFallback viewState={constructorState} what="Constructor standings" />)}
            {tab === "records" && (recordState === "list" ? <Records records={rec.records} /> : <StandingsFallback viewState={recordState} what="Records" />)}
        </div>
    );
}

function DriverStandings({ rows }) {
    const max = rows[0]?.points || 1;
    return (
        <div className="rounded-2xl glass-strong border border-white/5 overflow-hidden">
            <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/5 font-mono text-[10px] tracking-widest text-zinc-500 uppercase">
                <div className="col-span-1">Pos</div>
                <div className="col-span-4">Driver</div>
                <div className="col-span-3">Team</div>
                <div className="col-span-1 text-right">Wins</div>
                <div className="col-span-3 text-right">Points</div>
            </div>
            <div>
                {rows.map((d, i) => (
                    <Reveal key={d.id} delay={Math.min(i * 0.025, 0.2)}>
                        <div className="grid grid-cols-12 gap-4 px-6 py-4 items-center border-b border-white/5 hover:bg-white/5 transition-colors">
                            <div className="col-span-1 font-display text-2xl tabular-nums">{d.position}</div>
                            <div className="col-span-4 flex items-center gap-3">
                                <span className="h-6 w-1 rounded-sm" style={{ background: d.team_color }} />
                                <div>
                                    <div className="font-semibold">{d.full_name}</div>
                                    <div className="text-[10px] font-mono text-zinc-500 tracking-wider">{d.code} · #{d.number}</div>
                                </div>
                            </div>
                            <div className="col-span-3 text-sm text-zinc-400">{d.team_name}</div>
                            <div className="col-span-1 text-right tabular-nums">{d.wins}</div>
                            <div className="col-span-3">
                                <div className="flex items-center justify-end gap-3">
                                    <div className="hidden md:block flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                                        <div className="h-full rounded-full" style={{ width: `${(d.points / max) * 100}%`, background: d.team_color }} />
                                    </div>
                                    <div className="font-display text-2xl tabular-nums w-16 text-right">{d.points}</div>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    );
}

function ConstructorStandings({ rows }) {
    const max = rows[0]?.points || 1;
    return (
        <div className="rounded-2xl glass-strong border border-white/5 overflow-hidden">
            <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/5 font-mono text-[10px] tracking-widest text-zinc-500 uppercase">
                <div className="col-span-1">Pos</div>
                <div className="col-span-5">Team</div>
                <div className="col-span-2 text-right">Wins</div>
                <div className="col-span-4 text-right">Points</div>
            </div>
            <div>
                {rows.map((t, i) => (
                    <Reveal key={t.id} delay={Math.min(i * 0.03, 0.2)}>
                        <div className="grid grid-cols-12 gap-4 px-6 py-5 items-center border-b border-white/5 hover:bg-white/5 transition-colors">
                            <div className="col-span-1 font-display text-2xl tabular-nums">{t.position}</div>
                            <div className="col-span-5 flex items-center gap-3">
                                <span className="h-8 w-1.5 rounded-sm" style={{ background: t.primary_color }} />
                                <div>
                                    <div className="font-display text-xl">{t.short_name}</div>
                                    <div className="text-xs text-zinc-500">{t.engine} · {t.chassis}</div>
                                </div>
                            </div>
                            <div className="col-span-2 text-right tabular-nums">{t.wins}</div>
                            <div className="col-span-4">
                                <div className="flex items-center justify-end gap-3">
                                    <div className="hidden md:block flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                                        <div className="h-full rounded-full" style={{ width: `${(t.points / max) * 100}%`, background: t.primary_color }} />
                                    </div>
                                    <div className="font-display text-2xl tabular-nums w-16 text-right">{t.points}</div>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    );
}

function Records({ records }) {
    const items = Object.entries(records);
    return (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(([key, val]) => (
                <Reveal key={key}>
                    <div className="p-6 rounded-xl bg-graphite-800/60 border border-white/5">
                        <div className="flex items-center gap-2 mb-3">
                            <Trophy className="h-4 w-4 text-amber-400" />
                            <span className="label-eyebrow">{key.replace(/_/g, " ")}</span>
                        </div>
                        <div className="font-display text-3xl tracking-tight">{val.value}</div>
                        <div className="mt-2 text-sm text-zinc-400">{val.driver || val.team || "—"}</div>
                    </div>
                </Reveal>
            ))}
        </div>
    );
}
